import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FONT } from '../welcome-tour/tourTheme';
import { focusRingSx } from '../welcome-tour/components/TourPrimitives';

// Per-size metrics, px. The underline is the tab's bottom edge, so padBottom
// is the text-to-line gap; padTop restores the tap target (the tab has no fill).
export const TEXT_TAB = {
  md: { font: 13, padTop: 16, padBottom: 7, gap: 18, tracking: 0 },
  lg: { font: 18, padTop: 17, padBottom: 9, gap: 22, tracking: '-.01em' }
} as const;
export type TextTabSize = keyof typeof TEXT_TAB;
/** Space between a term label and the tab row under it. */
export const TEXT_TAB_LABEL_GAP = 4;

/** Vertical centre of a tab's text, from the top of a tab row. */
export const tabTextMidline = (size: TextTabSize): number => TEXT_TAB[size].padTop + TEXT_TAB[size].font / 2;

/** What a surface supplies: the tour its palette, the app its MUI theme. */
export interface TextTabLook {
  /** Underline, focus ring and the chosen tab's note. */
  accent: string;
  /** Rest opacity of an unchosen tab. */
  offOpacity: number;
  /** Text colour; 'inherit' where the surface already sets it. */
  color: string;
}

const useThemeTextTabLook = (): TextTabLook => {
  const { palette } = useTheme();
  return { accent: palette.primary.main, offOpacity: 0.5, color: palette.text.primary };
};

/**
 * A row of text tabs: the chosen one lit and underlined, the rest dim, an
 * optional mono note beside each word. One control for the tour's ticket, the
 * lock panel and the quick-subscribe dialog, so a choice looks the same
 * wherever it is made.
 */
export const TextTabs = <T extends string>({
  look: lookProp,
  label,
  value,
  onChange,
  options,
  size = 'md'
}: {
  /** Defaults to the app theme. */
  look?: TextTabLook;
  label: string;
  /** null: nothing chosen yet. */
  value: T | null;
  onChange: (v: T) => void;
  options: Array<{ key: T; label: string; note?: string; disabled?: boolean }>;
  size?: TextTabSize;
}) => {
  const themeLook = useThemeTextTabLook();
  const look = lookProp ?? themeLook;
  const m = TEXT_TAB[size];
  return (
    <Box role="group" aria-label={label} sx={{ display: 'flex', flexWrap: 'wrap', columnGap: `${m.gap}px`, rowGap: '2px' }}>
      {options.map((o) => {
        const on = o.key === value;
        return (
          <Box
            key={o.key}
            component="button"
            type="button"
            aria-pressed={on}
            disabled={o.disabled}
            onClick={() => onChange(o.key)}
            sx={{
              all: 'unset',
              boxSizing: 'border-box',
              cursor: o.disabled ? 'not-allowed' : 'pointer',
              display: 'inline-flex',
              alignItems: 'baseline',
              gap: '7px',
              p: `${m.padTop}px 0 ${m.padBottom}px`,
              font: `600 ${m.font}px/1 ${FONT.display}`,
              letterSpacing: m.tracking,
              color: look.color,
              opacity: o.disabled ? 0.3 : on ? 1 : look.offOpacity,
              boxShadow: on ? `inset 0 -2px 0 ${look.accent}` : 'inset 0 -2px 0 transparent',
              transition: 'opacity .15s ease, box-shadow .15s ease',
              '&:hover': { opacity: o.disabled ? 0.3 : 1 },
              ...focusRingSx(look.accent)
            }}
          >
            {o.label}
            {o.note && (
              <Box
                component="span"
                sx={{
                  font: `500 10px/1 ${FONT.mono}`,
                  letterSpacing: '.04em',
                  color: on ? look.accent : 'inherit',
                  opacity: on ? 1 : 0.8,
                  whiteSpace: 'nowrap'
                }}
              >
                {o.note}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};
